/** @jsxImportSource @emotion/react */
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import Checkbox from 'components/atoms/IndeterminateCheckbox'
import { skipToken } from '@reduxjs/toolkit/query/react'
import Toast from 'components/atoms/Toast'
import { CMS1500DownloadModal } from 'components/lab/billingCharge/CMS1500DownloadModal'
import { claimsReachPayers } from 'config/app'
import { ChargeCommentsSection } from 'components/lab/billingCharge/sections/ChargeCommentsSection'
import { formatCalendarDate, formatEasternDate } from 'helpers/dates'
import { formalName } from 'helpers/nameHelper'
import { ChargeFilesSection } from 'components/lab/billingCharge/sections/ChargeFilesSection'
import { ChargeInfoSection } from 'components/lab/billingCharge/sections/ChargeInfoSection'
import { ChargeLogsSection } from 'components/lab/billingCharge/sections/ChargeLogsSection'
import { ChargeOptumClaimStatusBanner } from 'components/lab/billingCharge/sections/ChargeOptumClaimStatusBanner'
import { ChargeDenials } from 'components/lab/billingDenial/ChargeDenials'
import { NeedsWorkChargeSection } from 'components/lab/billingNeedsWork/NeedsWorkChargeSection'
import { NeedsWorkFlagModal } from 'components/lab/billingNeedsWork/NeedsWorkFlagModal'
import { useGetChargeNeedsWorkQuery } from 'store/services/needsWork'
import { ChargeProceduresSection } from 'components/lab/billingCharge/sections/ChargeProceduresSection'
import { SubmissionMethodControlProps } from 'components/lab/billingCharge/sections/SubmissionMethodControl'
import {
  ClaimPayerRank,
  describeCobSubmissionError,
  getBillingTestTypeLabel,
  getClaimSubmissionMethod,
  getCobConfirmOptions,
  getCobResubmitConfirmOptions,
  isPaperClaim,
  isPayerIdentityLocked,
  ProcedureFormRow,
  toProcedureFormRows,
  toSavedProcedures,
} from 'components/lab/billingCharge/utils'
import { SearchParams } from 'components/lab/order/Order'
import { confirm } from 'components/molecules/Confirmation'
import { Formik, FormikHelpers } from 'formik'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useCreateBillingChargeMutation,
  useDeleteBillingChargeMutation,
  useGetBillingChargeQuery,
  useSubmitBillingChargesMutation,
  useUpdateBillingChargeMutation,
} from 'store/services/billingCharges'
import { useGetLabOrdersQuery } from 'store/services/labOrder'
import { useGetPatientQuery } from 'store/services/patient'
import * as Yup from 'yup'
import React, { useMemo, useState } from 'react'
import DashboardContent from 'components/lab/atoms/DashboardContent'
import routes from 'components/lab/constants/routes'
import { Button } from 'react-bootstrap'
import { useGetAllBillingProceduresListQuery } from 'store/services/billing-procedures'
import { useGetLabPhysiciansQuery } from 'store/services/labPhysician'
import { useGetLabPayersQuery } from 'store/services/payer'
import { useGetChargeDenialsQuery } from 'store/services/billingDenials'
import { OPEN_DENIAL_STATUSES } from 'components/lab/billingDenial/consts'
import { BillingChargeDto, BillingChargeProcedure, CreateBillingChargeDto, Physician, } from 'store/types'
import { useDebounce } from 'use-debounce'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from 'components/lab/user/permissions'

export const ChargeSection = ({
  title,
  children,
  defaultOpen = true,
}: {
  title: string | React.ReactNode
  children: React.ReactNode
  /** Sections that are reference material rather than part of the form start closed. */
  defaultOpen?: boolean
}) => {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div
      className={'p-3'}
      css={{
        background: '#eef0f3',
        borderRadius: 5,
        marginBottom: 15,
      }}>
      <div
        className={'d-flex align-items-center'}
        css={{ fontWeight: 600, marginBottom: open ? 10 : 0, '& > svg': { cursor: 'pointer' } }}>
        {open ? (
          <KeyboardArrowDownIcon onClick={() => setOpen(!open)} />
        ) : (
          <KeyboardArrowUpIcon onClick={() => setOpen(!open)} />
        )}{' '}
        <span
          css={{
            '& > *': {
              fontSize: '18px !important',
            },
            fontSize: '18px !important',
          }}>
          {title}
        </span>
      </div>
      {open && <div>{children}</div>}
    </div>
  )
}

const initialSearch: SearchParams = {
  orderId: '',
  patientFirstName: '',
  patientLastName: '',
  patientDob: '',
  physicianIds: '',
  physicianName: '',
  verified: false,
  aobVerified: false,
  hasUploads: false,
}

export type BillingChargeFormValues = {
  placeOfService: string
  renderingAndBilling?: string
  originalRef?: string
  pan?: string
  claimType?: string
  insuranceTypeCode?: string
  patientId: string
  type: string
  primaryPayerId?: string
  secondaryPayerId?: string
  primaryMemberId?: string
  primaryGroupId?: string
  secondaryMemberId?: string
  secondaryGroupId?: string
  locationCode?: string
  claimStatus?: string
  referringPhysicianNpi?: string
  referringPhysicianName?: string
  procedures: ProcedureFormRow[]
  diagnosisCodes: string[]
} & Partial<BillingChargeDto>

const blankProcedure: BillingChargeProcedure = {
  cptCode: '',
  startDos: '',
  endDos: '',
  // Optum rejects claims with 0 units of service (SV104), so new lines start at 1.
  unitsValue: 1,
  modifier: '',
  units: 'UN',
  dxPointers: '',
  planAmount: 0,
  patientAmount: 0,
}

const dxPointerRegex = /^(?:\s*[1-4]\s*)(?:\s*,\s*[1-4]\s*)*$/

const procedureSchema = Yup.object().shape({
  cptCode: Yup.string().required('Required'),
  startDos: Yup.string().required('Required'),
  endDos: Yup.string().required('Required'),
  dxPointers: Yup.string().required('Required').matches(dxPointerRegex, 'Invalid Dx. Ptr'),
  unitsValue: Yup.number().required('Required').moreThan(0, 'Must be > 0'),
  units: Yup.string().required('Required'),
  patientAmount: Yup.number().required('Required'),
  planAmount: Yup.number().required('Required'),
})

const validationSchema = Yup.object().shape({
  placeOfService: Yup.string().required('Required'),
  primaryPayerId: Yup.string().when('type', {
    is: (type: string) => type === 'DEFAULT',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema,
  }),
  primaryMemberId: Yup.string().when('type', {
    is: (type: string) => type === 'DEFAULT',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema,
  }),
  locationCode: Yup.string().required('Required'),
  claimType: Yup.string().when('type', {
    is: (type: string) => type === 'DEFAULT',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema,
  }),
  renderingAndBilling: Yup.string().required('Required'),
  procedures: Yup.array()
    .transform((value) => value.slice(0, value.length - 1))
    .of(procedureSchema)
    .min(1, 'At least one procedure is required'),
})

const initialValues: BillingChargeFormValues = {
  placeOfService: '11',
  renderingAndBilling: undefined,
  originalRef: '',
  pan: '',
  patientId: '',
  type: 'DEFAULT',
  claimType: 'ORIGINAL',
  insuranceTypeCode: '',
  primaryPayerId: undefined,
  primaryPayerName: '',
  secondaryPayerId: undefined,
  secondaryPayerName: '',
  locationCode: '',
  claimStatus: '',
  referringPhysicianNpi: '',
  referringPhysicianName: '',
  procedures: [blankProcedure],
  diagnosisCodes: [],
}

const SUBMISSION_METHOD_FIELD: Record<ClaimPayerRank, keyof BillingChargeDto> = {
  primary: 'claimSubmissionMethod',
  secondary: 'secondaryClaimSubmissionMethod',
  tertiary: 'tertiaryClaimSubmissionMethod',
}

const SUBMISSION_METHOD_PAYER_NAME: Record<ClaimPayerRank, (charge: BillingChargeDto) => string | null | undefined> = {
  primary: (charge) => charge.primaryPayerName,
  secondary: (charge) => charge.secondaryPayerName,
  tertiary: (charge) => charge.tertiaryPayerName,
}

const SUBMISSION_METHOD_PAYER_VERDICT: Record<ClaimPayerRank, (charge: BillingChargeDto) => string | null | undefined> = {
  primary: (charge) => charge.optumClaimStatus?.status,
  secondary: (charge) => charge.secondaryOptumClaimStatus?.status,
  tertiary: (charge) => charge.tertiaryOptumClaimStatus?.status,
}

export const BillingChargeUpsert = () => {
  const [search, setSearch] = useState<SearchParams>(initialSearch)
  const [debouncedSearch] = useDebounce(search, 500)
  const navigate = useNavigate()
  const { chargeId: id, patientId } = useParams()
  // An open denial unlocks its payer, so a claim denied for the wrong payer can be rebilled.
  const { data: denials } = useGetChargeDenialsQuery(id && patientId ? { chargeId: id, patientId: Number(patientId) } : skipToken)
  const hasOpenDenialAt = (position: number) =>
    !!denials?.some((denial) => OPEN_DENIAL_STATUSES.includes(denial.status) && Number(denial.payerPosition) === position)
  const { data: patient } = useGetPatientQuery(patientId ? { id: Number(patientId) } : skipToken)

  const canAddCharge = useLabPermission(UserPermission.ADD_BILLING_CHARGES)
  const canModifyCharge = useLabPermission(UserPermission.MODIFY_BILLING_CHARGES)
  const canRemoveCharge = useLabPermission(UserPermission.REMOVE_BILLING_CHARGES)
  const canSubmitCharge = useLabPermission(UserPermission.SUBMIT_BILLING_CHARGES)
  const readOnly = id ? !canModifyCharge : !canAddCharge
  const [flaggingNeedsWork, setFlaggingNeedsWork] = useState(false)
  // Several problems, each with its own owner, can be open on one charge at once.
  const { data: needsWorkItems = [] } = useGetChargeNeedsWorkQuery(id ?? skipToken)
  const openNeedsWorkCount = needsWorkItems.filter((item) => item.status === 'OPEN').length

  const [createBillingCharge] = useCreateBillingChargeMutation()
  const [updateBillingCharge] = useUpdateBillingChargeMutation()
  const [deleteBillingCharge] = useDeleteBillingChargeMutation()
  const [submitBillingCharges] = useSubmitBillingChargesMutation()

  const [mocked, setMocked] = useState(false)
  const [showCMS1500Modal, setShowCMS1500Modal] = useState(false)
  // A COB claim goes to Optum inside the request: the button waits for the answer.
  const [cobSubmitting, setCobSubmitting] = useState(false)

  const { data: charge, refetch } = useGetBillingChargeQuery(
    id && patientId ? { id, patientId } : skipToken,
  )
  const { data: patientOrders } = useGetLabOrdersQuery(
    patientId
      ? ({
          patientId: Number(patientId),
          page: 0,
          size: 100,
        } as any)
      : skipToken,
  )

  const initValues = useMemo(() => {
    if (charge) {
      return {
        placeOfService: charge.placeOfService,
        // Sleep Solutions, LLC is retired; legacy charges edit as TestSmarter.
        renderingAndBilling: charge.renderingAndBilling === 'SLEEP_SOLUTIONS' ? 'TEST_SMARTER' : charge.renderingAndBilling,
        originalRef: charge.originalRef,
        pan: charge.pan,
        patientId: charge.patientId,
        orderId: charge.orderId,
        type: charge.type,
        insuranceTypeCode: charge.insuranceTypeCode || '',
        primaryPayerId: charge.primaryPayerId,
        primaryMemberId: charge.primaryMemberId,
        primaryGroupId: charge.primaryGroupId,
        secondaryMemberId: charge.secondaryMemberId,
        secondaryGroupId: charge.secondaryGroupId,
        primaryPayerName: charge.primaryPayerName,
        secondaryPayerId: charge.secondaryPayerId,
        secondaryPayerName: charge.secondaryPayerName,
        // Display only — the form edits positions 1 and 2 through the selects.
        payers: charge.payers,
        locationCode: charge.locationCode,
        claimStatus: charge.claimStatus,
        claimType: charge.claimType,
        referringPhysicianNpi: charge.referringPhysicianNpi,
        referringPhysicianName: charge.referringPhysicianName,
        procedures: [...toProcedureFormRows(charge.procedures), blankProcedure],
        diagnosisCodes: charge.diagnosisCodes,
      }
    } else {
      return initialValues
    }
  }, [charge])

  const { data: payers } = useGetLabPayersQuery({
    name: debouncedSearch?.payerName,
    active: true,
  })
  const { data: physicianList } = useGetLabPhysiciansQuery(
    typeof debouncedSearch?.physicianName === 'string' &&
      debouncedSearch?.physicianName?.length >= 2
      ? {
          name: debouncedSearch?.physicianName,
        }
      : skipToken,
  )
  const { data: procedures } = useGetAllBillingProceduresListQuery({})

  const payersOptions = useMemo(
    () =>
      payers?.map((p) => ({
        value: p.payerId,
        label: p.name,
        matchedName: p.matchedName,
        uPayerId: p.uPayerId,
      })),
    [payers],
  )

  const physicians = useMemo(() => {
    if (!Array.isArray(physicianList)) {
      return []
    }
    return physicianList
      .filter((physician: Physician, index: number, list: Physician[]): boolean => {
        const firstIndex = list.findIndex((p: Physician) => String(p.npi) === String(physician.npi))
        return firstIndex === index
      })
      .sort((a: Physician, b: Physician) => {
        return a.lastName === b.lastName
          ? String(a.firstName).localeCompare(b.firstName)
          : String(a.lastName).localeCompare(b.lastName)
      })
  }, [physicianList])

  const physicianOptions = useMemo(
    () => physicians?.map((p) => ({ value: p.npi, label: formalName(p.firstName, p.lastName) })),
    [physicians],
  )

  const procedureOptions = useMemo(
    () =>
      procedures?.map((p) => ({
        value: p.cptCode,
        label: `${p.cptCode} - ${p.name}`,
      })),
    [procedures],
  )

  const orderOptions = useMemo(
    () =>
      (patientOrders?.items ?? []).map((order: any) => ({
        value: String(order.id),
        label: `#${order.id} - ${getBillingTestTypeLabel(order.testType || order.test_type) || 'Unknown'}${order.completedAt ? ` - ${formatEasternDate(order.completedAt)}` : ''}`,
      })),
    [patientOrders?.items],
  )

  const backToPatient = () => {
    navigate(`${routes.index}${routes.patient.root}/${patientId}`)
  }

  const onSubmit = async (
    values: BillingChargeFormValues,
    { resetForm }: FormikHelpers<BillingChargeFormValues>,
  ) => {
    const diagnosisCodes = values.diagnosisCodes.filter(Boolean)

    try {
      const trimmedProcedures = toSavedProcedures(values.procedures.slice(0, values.procedures.length - 1))
      // Sending the chain back would overrule the plan selects with the values
      // the form loaded. Left out, the server merges this edit into the stored
      // chain — which is also what keeps a tertiary payer alive.
      const { payers: _displayOnlyPayers, ...editedValues } = values
      const payload = {
        ...editedValues,
        patientId: charge?.patientId || patientId || '',
        orderId: id
          ? (values.orderId ? String(values.orderId) : null)
          : (values.orderId ? String(values.orderId) : undefined),
        diagnosisCodes,
        procedures: trimmedProcedures,
      }

      if (!payload.patientId) {
        return
      }

      if (id) {
        await updateBillingCharge({
          id,
          ...payload,
        }).unwrap()
        resetForm({
          values: {
            ...values,
            patientId: payload.patientId,
            orderId: payload.orderId as BillingChargeFormValues['orderId'],
            diagnosisCodes,
            procedures: [...toProcedureFormRows(trimmedProcedures), blankProcedure],
          },
        })
        await refetch()
      } else {
        await createBillingCharge(payload as CreateBillingChargeDto).unwrap()
        backToPatient()
      }

      Toast({ type: 'success', label: `Charge ${id ? 'updated' : 'created'}!` })
    } catch (err) {
      Toast({ type: 'error', label: err?.data?.message })
    }
  }

  const changeSearch = (field: string, value: string | boolean) => {
    setSearch((p) => ({ ...p, [field]: value }))
  }

  const chargeType = (charge?.type || initValues.type)
  const isQuickCharge = chargeType === 'QUICK' || chargeType === 'PATIENT'
  const lockAllFields = readOnly
  // The primary claim's method; each COB hop has its own (see cobIsPaper).
  const isPaperSubmission = isPaperClaim(charge, 'primary')
  const hasSecondaryPayer = charge?.type === 'DEFAULT' && !!charge?.secondaryPayerId
  const hasTertiaryPayer = charge?.type === 'DEFAULT'
    && (charge?.payers ?? []).some((payer) => Number(payer.position) === 3)
  // The ranks the saved charge has a payer at, which are the only ones with a
  // claim to send and so the only ones whose method is shown.
  const claimRanks: ClaimPayerRank[] = charge?.type === 'DEFAULT'
    ? [
      ...(charge?.primaryPayerId ? ['primary' as const] : []),
      ...(hasSecondaryPayer ? ['secondary' as const] : []),
      ...(hasTertiaryPayer ? ['tertiary' as const] : []),
    ]
    : []
  // The modal offers every payer's form; it is only worth opening when at
  // least one of them bills on paper.
  const canDownloadCMS1500 = !!id && !!patientId && claimRanks.some((rank) => isPaperClaim(charge, rank))
  // The hop the action targets and whether it still needs a claim, from the
  // charge's responsibility projection. The local derivation stands in for a
  // charge whose projection has not been written yet.
  const cobRank: 'secondary' | 'tertiary' = charge?.cobRank
    || (hasTertiaryPayer && charge?.secondarySubmissionStatus === 'SUBMITTED' ? 'tertiary' : 'secondary')
  const cobSubmissionStatus = cobRank === 'tertiary'
    ? charge?.tertiarySubmissionStatus
    : charge?.secondarySubmissionStatus
  const isCobSubmitted = cobSubmissionStatus === 'SUBMITTED'
  // On its way to Optum from another tab or biller: sending it again now
  // would be a duplicate.
  const isCobInFlight = cobSubmissionStatus === 'PENDING_SUBMISSION' && !isPaperSubmission
  const isCrossedOver = charge?.cobStatus === 'CROSSED_OVER'
  const cobLabel = cobRank === 'tertiary' ? 'Tertiary COB claim' : 'COB claim'
  // Every claim of the charge that has gone out, in waterfall order, for the
  // one success block the page shows: a COB claim adds a line to it rather
  // than a block of its own. The rank's own date, else its last logged send.
  const lastLoggedSubmission = (rank: 'primary' | 'secondary' | 'tertiary') => [...(charge?.submissionLog || [])]
    .reverse()
    .find((entry) => entry.payerRank === rank)?.submittedAt
  const submittedClaims = [
    {
      rank: 'primary' as const,
      label: 'Primary claim',
      status: charge?.submissionStatus,
      payerName: charge?.primaryPayerName,
      submittedAt: charge?.submittedAt,
    },
    {
      rank: 'secondary' as const,
      label: 'Secondary COB claim',
      status: charge?.secondarySubmissionStatus,
      payerName: charge?.secondaryPayerName,
      submittedAt: charge?.secondarySubmittedAt,
    },
    {
      rank: 'tertiary' as const,
      label: 'Tertiary COB claim',
      status: charge?.tertiarySubmissionStatus,
      payerName: charge?.tertiaryPayerName,
      submittedAt: charge?.tertiarySubmittedAt,
    },
  ]
    .filter((claim) => claim.status === 'SUBMITTED')
    .map((claim) => ({
      ...claim,
      isPaper: isPaperClaim(charge, claim.rank),
      submittedAt: claim.submittedAt || lastLoggedSubmission(claim.rank),
    }))
  // Each rank goes out its own way, so the block names a paper claim among
  // electronic ones rather than calling them all sent to Optum.
  const hasPaperClaim = submittedClaims.some((claim) => claim.isPaper)
  const hasElectronicClaim = submittedClaims.some((claim) => !claim.isPaper)
  // The next COB hop goes out the way its own payer takes claims, whatever
  // the primary did.
  const cobIsPaper = isPaperClaim(charge, cobRank)

  const buildChargeUpdatePayload = (
    baseCharge: BillingChargeDto,
    overrides: Partial<BillingChargeDto> = {},
  ): Partial<BillingChargeDto> => ({
    id: baseCharge.id,
    patientId: baseCharge.patientId,
    orderId: baseCharge.orderId,
    status: baseCharge.status,
    placeOfService: baseCharge.placeOfService,
    renderingAndBilling: (baseCharge.renderingAndBilling as string) === 'SLEEP_SOLUTIONS'
      ? ('TEST_SMARTER' as typeof baseCharge.renderingAndBilling)
      : baseCharge.renderingAndBilling,
    originalRef: baseCharge.originalRef,
    referringPhysicianNpi: baseCharge.referringPhysicianNpi,
    referringPhysicianName: baseCharge.referringPhysicianName,
    pan: baseCharge.pan,
    claimType: baseCharge.claimType,
    insuranceTypeCode: baseCharge.insuranceTypeCode,
    primaryPayerId: baseCharge.primaryPayerId,
    primaryPayerName: baseCharge.primaryPayerName,
    claimSubmissionMethod: baseCharge.claimSubmissionMethod,
    secondaryPayerId: baseCharge.secondaryPayerId,
    secondaryPayerName: baseCharge.secondaryPayerName,
    primaryMemberId: baseCharge.primaryMemberId,
    primaryGroupId: baseCharge.primaryGroupId,
    secondaryMemberId: baseCharge.secondaryMemberId,
    secondaryGroupId: baseCharge.secondaryGroupId,
    locationCode: baseCharge.locationCode,
    claimStatus: baseCharge.claimStatus,
    type: baseCharge.type,
    procedures: baseCharge.procedures || [],
    diagnosisCodes: baseCharge.diagnosisCodes || [],
    ...overrides,
  })

  const switchSubmissionMethod = async (rank: ClaimPayerRank) => {
    if (!charge) {
      return
    }

    const nextSubmissionMethod = isPaperClaim(charge, rank) ? 'ELECTRONIC' : 'PAPER'
    const nextLabel = nextSubmissionMethod === 'PAPER' ? 'Paper' : 'Electronic'
    const payerName = SUBMISSION_METHOD_PAYER_NAME[rank](charge) || `the ${rank} payer`
    // The server moves a rejected claim back to pending when it switches, so
    // it is sent again the new way.
    const isRejected = SUBMISSION_METHOD_PAYER_VERDICT[rank](charge) === 'REJECTED'

    confirm('', {
      title: `Switch the ${rank} claim to ${nextLabel}`,
      description: (nextSubmissionMethod === 'PAPER'
        ? `The ${rank} claim to ${payerName} will be printed on a CMS-1500, mailed, and marked submitted `
          + 'by hand instead of being sent to Optum.'
        : `The ${rank} claim to ${payerName} will be sent to Optum electronically instead of on paper.`)
        + (isRejected ? ' The payer rejected it, so it goes back to pending to be sent again.' : ''),
      yesAction: `Yes, switch to ${nextLabel}`,
      noAction: 'Cancel',
    })
      .then(async () => {
        try {
          await updateBillingCharge(
            buildChargeUpdatePayload(charge, { [SUBMISSION_METHOD_FIELD[rank]]: nextSubmissionMethod }),
          ).unwrap()
          await refetch()
          Toast({ type: 'success', label: `The ${rank} claim will be submitted on ${nextLabel.toLowerCase()}.` })
        } catch (e) {
          const message = (e as { data?: { message?: string } })?.data?.message
          Toast({ type: 'error', label: message || 'Failed to switch submission method' })
        }
      })
      .catch(() => {})
  }

  const togglePaperSubmittedStatus = async () => {
    if (!charge) {
      return
    }

    const willUnmark = charge.submissionStatus === 'SUBMITTED'
    const nextStatus = willUnmark ? 'DRAFT' : 'SUBMITTED'

    confirm('', {
      title: willUnmark ? 'Un-mark as submitted' : 'Mark as submitted',
      description: willUnmark
        ? 'This will move the charge back to pending.'
        : 'This will mark the paper claim as submitted.',
      yesAction: willUnmark ? 'Yes, un-mark' : 'Yes, mark submitted',
      noAction: 'Cancel',
    })
      .then(async () => {
        try {
          await updateBillingCharge(
            buildChargeUpdatePayload(charge, { status: nextStatus }),
          ).unwrap()
          await refetch()
          Toast({
            type: 'success',
            label: willUnmark
              ? 'Charge moved back to pending.'
              : 'Charge marked as submitted.',
          })
        } catch (e) {
          const message = (e as { data?: { message?: string } })?.data?.message
          Toast({ type: 'error', label: message || 'Failed to update charge status' })
        }
      })
      .catch(() => {})
  }

  // Paper COB claims are never sent to Optum, so marking and un-marking them is
  // a straight charge update — mirroring the primary paper claim's toggle.
  const toggleCobPaperSubmittedStatus = async () => {
    if (!charge) {
      return
    }

    const isTertiaryHop = cobRank === 'tertiary'
    const hopPayerName = (isTertiaryHop ? charge.tertiaryPayerName : charge.secondaryPayerName)
      || `the ${cobRank} payer`
    const willUnmark = isTertiaryHop
      ? charge.tertiarySubmissionStatus === 'SUBMITTED'
      : charge.secondarySubmissionStatus === 'SUBMITTED'

    confirm('', {
      title: willUnmark ? `Un-mark ${cobLabel} as submitted` : `Mark ${cobLabel} as submitted`,
      description: willUnmark
        ? 'This will move the paper coordination-of-benefits claim back to pending.'
        : 'This will mark the paper coordination-of-benefits claim to '
          + `${hopPayerName} as submitted.`,
      yesAction: willUnmark ? 'Yes, un-mark' : 'Yes, mark submitted',
      noAction: 'Cancel',
    })
      .then(async () => {
        try {
          await updateBillingCharge(
            buildChargeUpdatePayload(charge, (isTertiaryHop
              ? {
                tertiarySubmissionStatus: willUnmark ? 'PENDING_SUBMISSION' : 'SUBMITTED',
                tertiarySubmittedAt: willUnmark ? null : new Date().toISOString(),
                tertiaryClaimError: null,
              }
              : {
                secondarySubmissionStatus: willUnmark ? 'PENDING_SUBMISSION' : 'SUBMITTED',
                secondarySubmittedAt: willUnmark ? null : new Date().toISOString(),
                secondaryClaimError: null,
              }) as Partial<BillingChargeDto>),
          ).unwrap()
          await refetch()
          Toast({
            type: 'success',
            label: willUnmark
              ? 'COB claim moved back to pending.'
              : 'COB claim marked as submitted.',
          })
        } catch (e) {
          const message = (e as { data?: { message?: string } })?.data?.message
          Toast({ type: 'error', label: message || 'Failed to update COB claim status' })
        }
      })
      .catch(() => {})
  }

  const deleteCharge = async () => {
    if (!id || !patientId) {
      return
    }

    confirm('', {
      title: 'Delete Charge',
      description: 'The charge will be removed from the patient account and charge listings, and the patient balances will be recalculated.',
      yesAction: 'Yes, delete charge',
      noAction: 'Cancel',
    })
      .then(async () => {
        try {
          await deleteBillingCharge({ id, patientId }).unwrap()
          Toast({ type: 'success', label: 'Charge deleted.' })
          backToPatient()
        } catch (e) {
          const message = (e as { data?: { message?: string } })?.data?.message
          Toast({ type: 'error', label: message || 'Failed to delete charge' })
        }
      })
      .catch(() => {})
  }

  return (
    <div>
      <DashboardContent
        title={id ? 'Edit Charge' : 'Create Charge'}
        icon="fa fa-hdd"
        content={
          <div className="p-4">
            {id && flaggingNeedsWork && (
              <NeedsWorkFlagModal
                target={{ kind: 'charge', chargeId: id }}
                onClose={() => setFlaggingNeedsWork(false)}
              />
            )}
            {id && patientId && (
              <CMS1500DownloadModal
                chargeId={id}
                patientId={patientId}
                allowCob={hasSecondaryPayer}
                allowTertiary={hasTertiaryPayer}
                show={showCMS1500Modal}
                onClose={() => setShowCMS1500Modal(false)}
              />
            )}
            <Formik
              enableReinitialize
              initialValues={initValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}>
              {({
                values,
                dirty,
                submitCount,
                isValid,
                errors,
                touched,
                handleSubmit,
                setFieldValue,
              }) => (
                <>
                  {(() => {
                    const currentPlanTotal = Number(
                      (
                        values?.procedures?.reduce((acc, proc) => acc + Number(proc?.planAmount || 0), 0) || 0
                      ).toFixed(2),
                    )
                    const isSubmittedCharge = charge?.submissionStatus === 'SUBMITTED'
                    const isPatientResponsibilityState =
                      !isQuickCharge && !isSubmittedCharge && currentPlanTotal <= 0
                    const submissionMethodControls = Object.fromEntries(
                      claimRanks.map((rank): [ClaimPayerRank, SubmissionMethodControlProps] => [rank, {
                        method: getClaimSubmissionMethod(charge, rank),
                        locked: !!charge?.claimSubmissionMethodLocked?.[rank],
                        canSwitch: !readOnly,
                        disabledReason: dirty
                          ? 'Save your changes first'
                          : rank === 'primary' && isPatientResponsibilityState
                            ? 'The plan balance is $0.00, so no primary claim is sent'
                            : undefined,
                        onSwitch: () => switchSubmissionMethod(rank),
                      }]),
                    ) as Partial<Record<ClaimPayerRank, SubmissionMethodControlProps>>
                    const prepaymentAppliedAmount = Number(charge?.prepaymentAppliedAmount || 0)
                    const isZeroBalanceCharge =
                      !isSubmittedCharge
                      && (charge?.balanceStatus === 'PAID'
                        || charge?.submissionStatus === 'NOT_SUBMITTABLE')

                    return (
                  <form onSubmit={handleSubmit}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div
                        className="d-flex align-items-center justify-content-between"
                        style={{ gap: '30px' }}>
                        <div
                          css={{
                            fontWeight: 600,
                            fontSize: 20,
                          }}>
                          {formalName(patient?.firstName, patient?.lastName) || '-'}
                          {patient?.dob && (
                            <div css={{ fontWeight: 400, fontSize: 16, color: '#6c757d' }}>
                              DOB {formatCalendarDate(patient.dob)}
                            </div>
                          )}
                        </div>
                        {canDownloadCMS1500 && (
                          <Button
                            variant="primary"
                            type="button"
                            onClick={() => setShowCMS1500Modal(true)}>
                            Download CMS1500 form
                          </Button>
                        )}
                      </div>
                      {submittedClaims.length > 0 && (
                        <div
                          css={{
                            background: '#e0fde0',
                            borderRadius: '5px',
                            padding: '10px 20px',
                          }}>
                          <div css={{ fontWeight: 600 }}>
                            <i className="fas fa-check text-success mr-2" />
                            <span>
                              {!hasElectronicClaim
                                ? 'Marked as submitted'
                                : hasPaperClaim ? 'Submitted' : 'Successfully submitted to Optum'}
                            </span>
                          </div>
                          {submittedClaims.map(({ rank, label, payerName, submittedAt, isPaper }) => (
                            <div key={rank} css={{ fontSize: 14, marginLeft: 22 }}>
                              {[
                                hasPaperClaim && hasElectronicClaim
                                  ? `${label} (${isPaper ? 'paper' : 'Optum'})`
                                  : label,
                                payerName,
                                formatEasternDate(submittedAt, ''),
                              ].filter(Boolean).join(' · ')}
                            </div>
                          ))}
                        </div>
                      )}
                      <div
                        className="d-flex align-items-center justify-content-between text-plus"
                        style={{ gap: '15px' }}>
                        <Checkbox
                          checked={mocked}
                          disabled={lockAllFields}
                          onChange={() => {
                            setMocked((x) => !x)
                          }}
                          label="Mocked"
                          name="mocked"
                          id="mocked"
                        />
                        <label className="form-check-label mr-4" htmlFor="mocked">
                          Mocked?
                        </label>
                        <Button
                          variant="outline-secondary"
                          type="button"
                          onClick={() => {
                            backToPatient()
                          }}>
                          Back To Patient
                        </Button>
                        {id && canModifyCharge && (
                          <Button
                            variant="outline-danger"
                            type="button"
                            title={openNeedsWorkCount
                              ? `This charge already has ${openNeedsWorkCount} open Needs Work item${openNeedsWorkCount === 1 ? '' : 's'} — add another for a different problem or owner`
                              : 'Keep this charge on the Needs Work list until someone follows it up'}
                            onClick={() => setFlaggingNeedsWork(true)}>
                            <i className="fas fa-flag mr-1" />
                            {openNeedsWorkCount ? 'Add Needs Work' : 'Needs Work'}
                          </Button>
                        )}
                        {!readOnly && (
                          <Button
                            variant="outline-primary"
                            type="submit"
                            disabled={!dirty || !isValid || !patient}>
                            Save
                          </Button>
                        )}
                        {!readOnly && canSubmitCharge && charge?.type === 'DEFAULT' && isPaperSubmission && (
                          <Button
                            variant={'primary'}
                            type="button"
                            disabled={!id || isPatientResponsibilityState || dirty || isZeroBalanceCharge}
                            onClick={togglePaperSubmittedStatus}>
                            {charge?.submissionStatus === 'SUBMITTED'
                              ? 'Un-mark as submitted'
                              : 'Mark as Submitted'}
                          </Button>
                        )}
                        {canSubmitCharge && charge?.type === 'DEFAULT' && !isPaperSubmission && (
                          <Button
                            variant={'primary'}
                            type="button"
                            disabled={!id || charge?.status === 'QUEUED' || isPatientResponsibilityState || dirty || isZeroBalanceCharge}
                            onClick={() => {
                              const isResubmission = charge?.status === 'FAILED'
                                || charge?.submissionStatus === 'SUBMITTED'

                              confirm('', isResubmission
                                ? {
                                  title: 'Re-submit claim to Optum?',
                                  description:
                                    'This sends the claim to Optum again as a new submission, under a new Claim '
                                    + 'Identifier. Use it when the claim failed, the payer rejected it, or it has to '
                                    + 'be corrected; if the payer already processed it, set Claim Type to Replacement '
                                    + "(or Void) first so the payer's Claim Number is cited.",
                                  yesAction: 'Re-submit claim',
                                  noAction: 'Cancel',
                                }
                                : {
                                  title: 'Are you sure?',
                                  description:
                                    'This will submit the charge for claim creation to Optum.',
                                  yesAction: 'Yes, proceed',
                                  noAction: 'Cancel',
                                })
                                .then(async () => {
                                  if (!id) {
                                    return
                                  }

                                  try {
                                    // The charge page is where a claim is sent again on
                                    // purpose (a correction, a void, a fix after a rejection).
                                    await submitBillingCharges({ ids: [id], mocked, resubmit: true }).unwrap()
                                    await refetch()

                                    Toast({ type: 'success', label: `Charge submission queued!` })
                                  } catch (e) {
                                    Toast({ type: 'error', label: e.data.message })
                                  }
                                })
                                .catch(() => {})
                            }}>
                            {charge?.status === 'FAILED' || charge?.submissionStatus === 'SUBMITTED'
                              ? 'Re-'
                              : ''}
                            Submit to Optum
                          </Button>
                        )}
                        {!readOnly && canSubmitCharge && hasSecondaryPayer && cobIsPaper && (
                          <Button
                            variant={'primary'}
                            type="button"
                            disabled={!id || dirty}
                            onClick={toggleCobPaperSubmittedStatus}>
                            {isCobSubmitted
                              ? `Un-mark ${cobLabel} as submitted`
                              : `Mark ${cobLabel} as submitted`}
                          </Button>
                        )}
                        {canSubmitCharge && hasSecondaryPayer && !cobIsPaper && (
                          <Button
                            variant={isCrossedOver || isCobSubmitted ? 'outline-secondary' : 'primary'}
                            type="button"
                            title={isCrossedOver && !isCobSubmitted ? charge?.cobBlockReason || '' : undefined}
                            disabled={!id || isCobInFlight || cobSubmitting || dirty}
                            onClick={() => {
                              // Sent once, it can be sent again: the payer rejected it, or
                              // it has to be corrected.
                              const resubmit = isCobSubmitted

                              confirm('', resubmit
                                ? getCobResubmitConfirmOptions(charge, cobRank)
                                : getCobConfirmOptions(charge, cobRank))
                                .then(async () => {
                                  if (!id) {
                                    return
                                  }

                                  setCobSubmitting(true)

                                  try {
                                    await submitBillingCharges({ ids: [id], payerRank: cobRank, mocked, resubmit }).unwrap()

                                    Toast({
                                      type: 'success',
                                      label: `${cobLabel} ${resubmit ? 're-submitted' : 'submitted'} to Optum`,
                                    })
                                  } catch (e) {
                                    Toast({
                                      type: 'error',
                                      label: 'COB Claim Submission Failed',
                                      extra: describeCobSubmissionError(e),
                                    })
                                  } finally {
                                    setCobSubmitting(false)
                                    await refetch()
                                  }
                                })
                                .catch(() => {})
                            }}>
                            {cobSubmitting || isCobInFlight
                              ? `Submitting ${cobLabel}…`
                              : isCobSubmitted
                                ? `Re-submit ${cobLabel}`
                                : isCrossedOver ? `Submit ${cobLabel} anyway` : `Submit ${cobLabel}`}
                          </Button>
                        )}
                      </div>
                    </div>
                    <div
                      css={{
                        marginTop: 30,
                        color: 'black',
                      }}>
                      {!claimsReachPayers && !mocked && (
                        <div
                          css={{
                            background: '#fff3cd',
                            border: '1px solid #ffeeba',
                            color: '#856404',
                            borderRadius: '5px',
                            fontWeight: 600,
                            padding: '10px 20px',
                            marginBottom: 20,
                          }}>
                          <i className="fas fa-exclamation-triangle mr-2" />
                          <span>
                            Test environment: with Mocked off, a claim only goes through Optum's validation.
                            It catches most of what production would reject, but not everything. Nothing reaches
                            the payer, so no ERA or claim status will come back.
                          </span>
                        </div>
                      )}
                      {id && <NeedsWorkChargeSection chargeId={id} />}
                      {!!charge && !!patientId && (
                        <ChargeOptumClaimStatusBanner
                          charge={charge}
                          patientId={patientId}
                          onResolved={refetch}
                        />
                      )}
                      {!!charge && !!patientId && (
                        <ChargeDenials
                          chargeId={id}
                          patientId={patientId}
                          mocked={mocked}
                          resubmitDisabledReason={dirty ? 'Save the charge first' : undefined}
                        />
                      )}
                      {prepaymentAppliedAmount > 0 && (
                        <div
                          css={{
                            background: '#e0f0fd',
                            borderRadius: '5px',
                            fontWeight: 600,
                            padding: '10px 20px',
                            marginBottom: 20,
                          }}>
                          <i className="fas fa-info-circle text-primary mr-2" />
                          <span>
                            Charged amount was reduced by ${prepaymentAppliedAmount.toFixed(2)} using
                            unapplied prepayments.
                            {isZeroBalanceCharge
                              && ' The plan balance is fully covered, so no claim needs to be submitted.'}
                          </span>
                        </div>
                      )}
                      {charge?.timelyFilingExpired && charge?.submissionStatus !== 'SUBMITTED' && (
                        <div
                          css={{
                            background: '#fff3cd',
                            border: '1px solid #ffeeba',
                            color: '#856404',
                            borderRadius: '5px',
                            fontWeight: 600,
                            padding: '10px 20px',
                            marginBottom: 20,
                          }}>
                          <i className="fas fa-exclamation-triangle mr-2" />
                          <span>
                            The timely filing window
                            {charge?.timelyFilingDays ? ` of ${charge.timelyFilingDays} days` : ''} for
                            this charge has passed. This claim can no longer be submitted to Optum.
                          </span>
                        </div>
                      )}
                      {charge?.submissionStatus !== 'SUBMITTED' && charge?.claimWarnings?.map((warning) => (
                        <div
                          key={warning}
                          css={{
                            background: '#fff3cd',
                            border: '1px solid #ffeeba',
                            color: '#856404',
                            borderRadius: '5px',
                            fontWeight: 600,
                            padding: '10px 20px',
                            marginBottom: 20,
                          }}>
                          <i className="fas fa-exclamation-triangle mr-2" />
                          <span>{warning}</span>
                        </div>
                      ))}
                      {/* Optum could not take the claim (a 500 or 503) and the queue is
                          trying again on its own; the charge stays queued. */}
                      {charge?.status === 'QUEUED' && charge?.claimError && (
                        <div
                          css={{
                            background: '#fff3cd',
                            borderRadius: '5px',
                            fontWeight: 600,
                            padding: '10px 20px',
                            marginBottom: 20,
                          }}>
                          <span>Optum was temporarily unavailable — the claim is being retried automatically</span>
                          <div
                            css={{
                              marginTop: 10,
                              fontWeight: 400,
                              fontSize: 14,
                            }}>
                            {charge?.claimError}
                          </div>
                        </div>
                      )}
                      {charge?.status === 'FAILED' && (
                        <div
                          css={{
                            background: '#fde0e0',
                            borderRadius: '5px',
                            fontWeight: 600,
                            padding: '10px 20px',
                            marginBottom: 20,
                          }}>
                          <span>Failed to submit charge to Optum</span>
                          <div
                            css={{
                              marginTop: 10,
                              fontWeight: 400,
                              fontSize: 14,
                            }}>
                            {charge?.claimError}
                          </div>
                        </div>
                      )}
                      {[
                        { label: 'COB Claim Submission Failed', error: charge?.secondaryClaimError },
                        { label: 'Tertiary COB Claim Submission Failed', error: charge?.tertiaryClaimError },
                      ].filter(({ error }) => !!error).map(({ label, error }) => (
                        <div
                          key={label}
                          css={{
                            background: '#fde0e0',
                            borderRadius: '5px',
                            fontWeight: 600,
                            padding: '10px 20px',
                            marginBottom: 20,
                          }}>
                          <span>{label}</span>
                          <div
                            css={{
                              marginTop: 10,
                              fontWeight: 400,
                              fontSize: 14,
                            }}>
                            {error}
                          </div>
                        </div>
                      ))}
                      {isCrossedOver && (
                        <div
                          css={{
                            background: '#e6f7fa',
                            border: '1px solid #b8e6ef',
                            borderRadius: '5px',
                            fontWeight: 600,
                            padding: '10px 20px',
                            marginBottom: 20,
                          }}>
                          <span>
                            {`Crossed over to ${charge?.responsiblePayerName || 'the next payer'}`}
                          </span>
                          <div css={{ marginTop: 10, fontWeight: 400, fontSize: 14 }}>
                            {`The payer forwarded this claim onward itself`}
                            {charge?.crossedOverAt
                              ? ` on ${formatEasternDate(charge.crossedOverAt)}`
                              : ''}
                            {`, so no COB claim is owed. Submit one only if the payer never pays.`}
                          </div>
                        </div>
                      )}
                      {isPatientResponsibilityState && (
                        <div
                          css={{
                            background: '#eef6ff',
                            border: '1px solid #cfe2ff',
                            borderRadius: '5px',
                            fontWeight: 600,
                            padding: '10px 20px',
                            marginBottom: 20,
                          }}>
                          <span>
                            Plan balance is $0.00. This charge will be treated as patient responsibility only until
                            plan balance is changed back to a positive amount and saved.
                          </span>
                        </div>
                      )}
                      <ChargeInfoSection
                        id={id}
                        errors={errors}
                        touched={touched}
                        values={values}
                        setFieldValue={setFieldValue}
                        search={search}
                        changeSearch={changeSearch}
                        payersOptions={payersOptions}
                        physicianOptions={physicianOptions}
                        orderOptions={orderOptions}
                        isPatientCharge={isQuickCharge}
                        disableAll={lockAllFields}
                        primaryLocked={isPayerIdentityLocked(
                          charge?.submissionStatus,
                          charge?.optumClaimStatus,
                          hasOpenDenialAt(1),
                        )}
                        secondaryLocked={isPayerIdentityLocked(
                          charge?.secondarySubmissionStatus,
                          charge?.secondaryOptumClaimStatus,
                          hasOpenDenialAt(2),
                        )}
                        payerClaimNumbers={[
                          { rank: 'primary', value: charge?.payerClaimNumber },
                          { rank: 'secondary', value: charge?.secondaryPayerClaimNumber },
                          { rank: 'tertiary', value: charge?.tertiaryPayerClaimNumber },
                        ].filter((entry): entry is { rank: string; value: string } => !!entry.value)}
                        submissionMethods={submissionMethodControls}
                      />
                      <fieldset disabled={lockAllFields}>
                        <ChargeProceduresSection
                          submitCount={submitCount}
                          setFieldValue={setFieldValue}
                          values={values}
                          touched={touched}
                          errors={errors}
                          blankProcedure={blankProcedure}
                          procedureOptions={procedureOptions}
                          disabled={lockAllFields}
                          isPatientCharge={isQuickCharge}
                          disablePlanAmount={isSubmittedCharge}
                          chargeSummary={charge ? {
                            appliedPatientAmount: charge.appliedPatientAmount,
                            appliedPlanAmount: charge.appliedPlanAmount,
                            currentPatientBalance: charge.currentPatientBalance,
                            currentPlanBalance: charge.currentPlanBalance,
                            writeOffAmount: charge.writeOffAmount,
                          } : undefined}
                        />
                      </fieldset>
                      {!!charge && (!!charge.files?.length || charge.type === 'DEFAULT') && (
                        <ChargeFilesSection
                          charge={charge}
                          patientId={patientId || charge.patientId}
                          mocked={mocked}
                          onRetried={refetch}
                        />
                      )}
                      <ChargeCommentsSection
                        claimStatusDisabled={isQuickCharge || lockAllFields || isPatientResponsibilityState}
                        chargeId={charge?.id}
                        notes={charge?.notes}
                        onNotesChange={refetch}
                      />
                      {!!charge && <ChargeLogsSection submissionLog={charge.submissionLog} chargeLog={charge.chargeLog} />}
                      {id && patientId && canRemoveCharge && (
                        <div className="d-flex justify-content-end mt-4">
                          <Button
                            variant="outline-danger"
                            type="button"
                            onClick={deleteCharge}>
                            Delete Charge
                          </Button>
                        </div>
                      )}
                    </div>
                  </form>
                    )
                  })()}
                </>
              )}
            </Formik>
          </div>
        }
        breadcrumb={[
          {
            label: 'Billing Charges',
            to: `${routes.index}${routes.device.root}`,
          },
          { label: id ? 'Edit' : 'New' },
        ]}
      />
    </div>
  )
}
